/* eslint-disable max-lines */
import { useCallback, useEffect, useRef } from "react";
import { useController, useWatch } from "react-hook-form";
import { Trash2, X } from "lucide-react";
import type { Lens } from "@hookform/lenses";
import { InchesInput } from "@design-lab/shared/ui/InchesInput";
import type { CustomSize } from "../../model/transferLabTypes";
import { QuantityInput } from "./QuantityInput";
import { PricingDisplay } from "./PricingDisplay";
import { useCustomSizePricing } from "../../hooks/useCustomSizePricing";

type CustomSizeRowProps = {
  sizeId: string;
  sizeLens: Lens<CustomSize>;
  size: CustomSize;
  showLabels: boolean;
  canRemove: boolean;
  onChange: (updates: Partial<CustomSize>) => void;
  onRemove: () => void;
  bounds?:
    | {
        minWidthInches: number;
        minHeightInches: number;
        maxWidthInches: number;
        maxHeightInches: number;
      }
    | undefined;
  aspectRatio?: number | undefined;
  artworkId?: string;
  transferTypeId?: string;
  enhancementTier?: string;
};

export function CustomSizeRow({
  sizeId,
  sizeLens,
  size,
  showLabels,
  canRemove,
  onChange,
  onRemove,
  bounds,
  aspectRatio,
  artworkId,
  transferTypeId,
  enhancementTier = "full_enhancements"
}: CustomSizeRowProps) {
  // Use real bounds from artwork metadata, fallback to defaults
  const minWidth = bounds?.minWidthInches ?? 1;
  const minHeight = bounds?.minHeightInches ?? 1;
  const maxWidth = bounds?.maxWidthInches ?? 20;
  const maxHeight = bounds?.maxHeightInches ?? 20;

  const widthLens = sizeLens.focus("width");
  const heightLens = sizeLens.focus("height");
  const widthInterop = widthLens.interop();
  const heightInterop = heightLens.interop();

  // Get controllers for each field
  const { field: widthField } = useController(widthInterop);
  const { field: heightField } = useController(heightInterop);

  // Watch values
  const widthValue = useWatch(widthInterop);
  const heightValue = useWatch(heightInterop);

  // Refs for syncing logic
  const lastUpdatedRef = useRef<"width" | "height" | null>(null);
  const aspectRatioRef = useRef(aspectRatio);
  const userFocusedFieldRef = useRef<"width" | "height" | null>(null);
  // Stable handlers to avoid remounts in masked inputs
  const handleWidthValueChange = useCallback(
    (num: number) => {
      if (userFocusedFieldRef.current !== "width") return;
      // Mark that width is the source of change; syncing handled in effect
      lastUpdatedRef.current = "width";
      onChange({ width: num });
    },
    [onChange]
  );

  const handleHeightValueChange = useCallback(
    (num: number) => {
      if (userFocusedFieldRef.current !== "height") return;
      lastUpdatedRef.current = "height";
      onChange({ height: num });
    },
    [onChange]
  );

  // Update aspect ratio ref
  useEffect(() => {
    if (aspectRatioRef.current !== aspectRatio) {
      aspectRatioRef.current = aspectRatio;
      lastUpdatedRef.current = null;
    }
  }, [aspectRatio]);

  // Clamp helper
  const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(value, max));

  // Sync height when width changes
  useEffect(() => {
    const currentRatio = aspectRatioRef.current;
    if (!currentRatio) return;
    const ratio = currentRatio as number;
    if (lastUpdatedRef.current === "height") {
      lastUpdatedRef.current = null;
      return;
    }
    if (userFocusedFieldRef.current !== "width") return;
    const widthNum = typeof widthValue === "number" ? widthValue : Number(widthValue as any);
    if (Number.isNaN(widthNum) || widthNum === 0) return;

    const newHeight = clamp(Math.round((widthNum / ratio) * 100) / 100, minHeight, maxHeight);

    const heightNum = typeof heightValue === "number" ? heightValue : Number(heightValue as any);

    if (Math.abs(heightNum - newHeight) > 0.01) {
      lastUpdatedRef.current = "width";
      heightField.onChange(newHeight);
      onChange({ height: newHeight });
      lastUpdatedRef.current = null;
    }
  }, [widthValue, minHeight, maxHeight, heightField, heightValue]);

  // Sync width when height changes
  useEffect(() => {
    const currentRatio = aspectRatioRef.current;
    if (!currentRatio) return;
    const ratio = currentRatio as number;
    if (lastUpdatedRef.current === "width") {
      lastUpdatedRef.current = null;
      return;
    }

    if (userFocusedFieldRef.current !== "height") return;
    const heightNum = typeof heightValue === "number" ? heightValue : Number(heightValue as any);
    if (Number.isNaN(heightNum) || heightNum === 0) return;

    const newWidth = clamp(Math.round(heightNum * ratio * 100) / 100, minWidth, maxWidth);

    const widthNum = typeof widthValue === "number" ? widthValue : Number(widthValue as any);

    if (Math.abs(widthNum - newWidth) > 0.01) {
      lastUpdatedRef.current = "height";
      widthField.onChange(newWidth);
      onChange({ width: newWidth });
      lastUpdatedRef.current = null;
    }
  }, [heightValue, minWidth, maxWidth, widthField, widthValue]);
  // Fetch pricing for this custom size when dimensions or quantity change
  const { pricing, isLoading } = useCustomSizePricing({
    artworkId: artworkId || "",
    transferTypeId: transferTypeId || "",
    sizeLens,
    enhancementTier,
    enabled: !!(artworkId && transferTypeId)
  });

  return (
    <div className="space-y-2">
      {showLabels && (
        <div className="grid grid-cols-[1fr_auto_1fr_1fr_auto] gap-2 text-xs font-medium text-gray-600">
          <span>Width</span>
          <span className="w-10" />
          <span>Height</span>
          <span className="text-center">Quantity</span>
          <span className="w-10" /> {/* Spacer for remove button */}
        </div>
      )}

      <div className="grid grid-cols-[1fr_auto_1fr_1fr_auto] items-start gap-2">
        <InchesInput
          id={`custom-width-${sizeId}`}
          lens={sizeLens.focus("width")}
          min={minWidth}
          max={maxWidth}
          placeholder="Width"
          data-testid="custom-size-width"
          className="h-10 rounded border border-gray-300 bg-white text-center text-base shadow-none outline-none"
          onFocus={() => {
            userFocusedFieldRef.current = "width";
          }}
          onBlur={() => {
            userFocusedFieldRef.current = null;
          }}
          onValueChange={handleWidthValueChange}
        />

        <div className="mt-4 flex items-center justify-center">
          <X className="size-2" />
        </div>

        <InchesInput
          id={`custom-height-${sizeId}`}
          lens={sizeLens.focus("height")}
          min={minHeight}
          max={maxHeight}
          placeholder="Height"
          data-testid="custom-size-height"
          className="h-10 rounded border border-gray-300 bg-white text-center text-base shadow-none outline-none"
          onFocus={() => {
            userFocusedFieldRef.current = "height";
          }}
          onBlur={() => {
            userFocusedFieldRef.current = null;
          }}
          onValueChange={handleHeightValueChange}
        />

        <div className="space-y-1">
          <QuantityInput
            id={`custom-quantity-${sizeId}`}
            lens={sizeLens.focus("quantity")}
            min={0}
            max={9999}
            onChange={quantity => onChange({ quantity })}
          />

          <div className="text-center text-xs">
            <PricingDisplay pricing={pricing} isLoading={isLoading} quantity={size.quantity} />
          </div>
        </div>

        {/* Remove button */}
        <button
          type="button"
          onClick={onRemove}
          disabled={!canRemove}
          className="flex h-10 w-10 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
          aria-label="Remove size"
          data-testid={`remove-custom-size-${sizeId}`}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
